// @ts-nocheck
// Ported byte-identically from obd3-gui v1
// (src/features/dashboard/components/DashboardShell.tsx).
// Dashboard page shell: title row + Customize button + KPI strip +
// tab bar + widget grid + Add-widget slide-over. Every tab has its own
// per-user localStorage layout. Role is resolved from Storage.user to
// match how the rest of Cloud Telephony v2 identifies the current user.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Plus, RefreshCw, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { getUser } from '@/utilities/setting';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { DASHBOARD_TABS, type TabId, type WidgetId } from '../config';
import { useDashboardLayout } from '../use-dashboard-layout';
import { useDashboardData } from '../DashboardDataContext';
import { AddWidgetSheet } from './AddWidgetSheet';
import { KpiStrip } from './KpiStrip';
import { WidgetGrid } from './WidgetGrid';
import Loader from '@/components/partials/components/Loader';

function useEffectiveRole(): string {
  const [role, setRole] = useState<string>(() => getUser()?.role ?? '');
  useEffect(() => {
    setRole(getUser()?.role ?? '');
  }, []);
  return role;
}

export function DashboardShell() {
  const role = useEffectiveRole();
  const {
    kpiLoading,
    dashboardStats,
    loadingForAgentStatusDistribution,
    kpiRefreshing,
    refreshKpis,
  } = useDashboardData();
  const visibleTabs = useMemo(
    () => DASHBOARD_TABS.filter((t) => t.roles.includes(role)),
    [role],
  );
  const [activeTab, setActiveTab] = usePersistedState<TabId>(
    'dashboard/DashboardShell:activeTab',
    visibleTabs[0]?.id ?? 'overview',
  );
  const [editing, setEditing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const { layout, reorder, setSize, add, remove, reset } =
    useDashboardLayout(activeTab);

  // If the user's first-visible tab changes (role hydrated late), jump to it.
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0]!.id);
    }
  }, [visibleTabs, activeTab]);

  // Full-page "Loading" indicator until BOTH the KPI aggregate fetch and
  // the agent-status distribution fetch have completed for the first
  // time. After we've bootstrapped once, subsequent polls / refreshes
  // let widgets handle their own per-tile skeletons so the whole page
  // doesn't blank out. `agentDataObject` is seeded with zeros so we
  // can't gate on its truthiness — track first-arrival with a ref.
  const bootstrappedRef = useRef(false);
  const kpiDone = !kpiLoading && !!dashboardStats;
  const agentDone = !loadingForAgentStatusDistribution;
  if (kpiDone && agentDone) bootstrappedRef.current = true;
  if (!bootstrappedRef.current) {
    return <Loader />;
  }

  const switchTab = (next: TabId) => {
    setActiveTab(next);
    setEditing(false);
    setAddOpen(false);
  };

  const stopEditing = () => {
    setEditing(false);
    setAddOpen(false);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4 lg:px-8 lg:py-6">
      <div className="flex flex-col gap-4">
        {/* Title row */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              Dashboard
            </h1>
            <p className="text-xs text-muted-foreground">
              Drag tiles in customize mode. Each tab's layout saves automatically.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!editing && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={refreshKpis}
                disabled={kpiRefreshing || kpiLoading}
                title="Refresh the stat tiles at the top"
              >
                <RefreshCw className={'h-3.5 w-3.5 ' + (kpiRefreshing ? 'animate-spin' : '')} />
                Refresh
              </Button>
            )}
            {editing && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={reset}
                  title="Reset this tab to defaults"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </Button>
                <Button type="button" size="sm" onClick={() => setAddOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Add widget
                </Button>
              </>
            )}
            {/* Refetch the KPI strip numbers */}
            <Button
              type="button"
              variant={editing ? 'default' : 'outline'}
              size="sm"
              onClick={() => (editing ? stopEditing() : setEditing(true))}
            >
              <Pencil className="h-3.5 w-3.5" />
              {editing ? 'Done' : 'Customize'}
            </Button>
          </div>
        </div>

        {/* Customize banner */}
        {editing && (
          <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="flex-1">
              <strong className="text-foreground">Customize mode.</strong>{' '}
              <span className="text-muted-foreground">
                Drag the grip handles to move tiles, click the size icon to
                resize, click ✕ on a tile to remove. Use{' '}
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Add widget
                </button>{' '}
                to bring tiles in.
              </span>
            </span>
          </div>
        )}

        {/* KPI strip — shares the same drag + resize mechanics as the tab grids */}
        <KpiStrip role={role} editing={editing} />

        {/* Tab bar */}
        <div className="flex items-center gap-1 overflow-x-auto border-b border-border">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => switchTab(t.id)}
                className={cn(
                  'relative flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors',
                  isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-x-2 bottom-0 h-[2px] rounded-full transition-colors',
                    isActive ? 'bg-primary' : 'bg-transparent',
                  )}
                />
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div>
          <WidgetGrid
            layout={layout}
            editing={editing}
            onReorder={reorder}
            onResize={setSize}
            onRemove={remove}
            role={role}
          />
        </div>
      </div>

      <AddWidgetSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        tab={activeTab}
        role={role}
        existingIds={layout.map((l) => l.id as WidgetId)}
        onAdd={(id) => {
          add(id);
        }}
      />
    </div>
  );
}
